import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import RecordNotFound from "../../core/components/RecordNotFound";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { printAgent } from "@/lib/print-agent";
import { toast } from "sonner";

// Subcomponents
import PrintersList from "./printers/PrintersList";
import DocumentRoutingMap from "./printers/DocumentRoutingMap";
import PrinterFormDialog from "./printers/PrinterFormDialog";
import PaperSizeFormDialog from "./printers/PaperSizeFormDialog";
import PaperSizeTemplateAssignmentsModal from "./printers/PaperSizeTemplateAssignmentsModal";
import TemplatePreviewModal from "./templatePreviewModal";
import {
  BLANK_PRINTER_FORM,
  BLANK_PAPER_FORM,
  PRINTER_ARCHETYPES,
  PAPER_SIZE_PRESETS,
  invalidateAllPrinterQueries,
} from "./printers/printerConstants";

import { Printer, PlusCircle, FileText, Bot, RotateCw } from "lucide-react";

const Printers = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (p) => {
    if (!permissions) return false;
    if (Array.isArray(p)) return p.some((perm) => permissions.includes(perm));
    return permissions.includes(p);
  };

  const canReadPrinters = hasPermission("printer read");
  const canCreatePrinters = hasPermission("printer create");
  const canUpdatePrinters = hasPermission("printer update");
  const canDeletePrinters = hasPermission("printer delete");

  useEffect(() => {
    if (!canReadPrinters) {
      // restricted access
    }
  }, [canReadPrinters, navigate]);

  const currentStoreId = useSelector((state) => state.current_store?.id ?? null);
  const currentStore = useSelector((state) => state.current_store);

  // Active Tab: "printers" | "routing"
  const [activeTab, setActiveTab] = useState("printers");

  // Printer Form Dialog State
  const [printerModalOpen, setPrinterModalOpen] = useState(false);
  const [printerEditMode, setPrinterEditMode] = useState(false);
  const [printerForm, setPrinterForm] = useState(BLANK_PRINTER_FORM);
  const [printerDeleteTarget, setPrinterDeleteTarget] = useState(null);

  // Paper Size Form Dialog State
  const [paperModalOpen, setPaperModalOpen] = useState(false);
  const [paperEditMode, setPaperEditMode] = useState(false);
  const [paperForm, setPaperForm] = useState(BLANK_PAPER_FORM);
  const [paperDeleteTarget, setPaperDeleteTarget] = useState(null);

  // Assignments Modal State
  const [assignmentsTargetPs, setAssignmentsTargetPs] = useState(null);
  const [assignmentsTargetPrinter, setAssignmentsTargetPrinter] = useState(null);

  // Template Preview Modal State
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // ── Query: ERF Print Agent Status ──────────────────────────────────────────
  const {
    data: pageAgentStatus,
    isLoading: pageAgentLoading,
    refetch: refetchPageAgent,
  } = useQuery({
    queryKey: ["page-print-agent-status", currentStore?.print_agent_pairing_key],
    queryFn: async () => {
      try {
        const isRunning = await printAgent.isAgentRunning();
        if (!isRunning) return { online: false, count: 0 };
        const res = await printAgent.getPrinters({
          pairingKey: currentStore?.print_agent_pairing_key,
        });
        return { online: true, count: res?.printers?.length || 0 };
      } catch {
        return { online: false, count: 0 };
      }
    },
    enabled: canReadPrinters,
    refetchInterval: 30000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // ── Query: Printers ────────────────────────────────────────────────────────
  const { data: printers = [], isLoading: printersLoading } = useQuery({
    queryKey: ["printers", currentStoreId],
    queryFn: async () => {
      const params = { perPage: 100 };
      if (currentStoreId) params.store_id = currentStoreId;
      const res = await apiClient.get(API_ENDPOINTS.PRINTERS.LIST, { params });
      return res.data?.data ?? [];
    },
    enabled: canReadPrinters,
  });

  // ── Query: All Paper Sizes ─────────────────────────────────────────────────
  const { data: allPaperSizes = [], isLoading: sizesLoading } = useQuery({
    queryKey: ["all-paper-sizes", printers.map((p) => p.id).join(",")],
    queryFn: async () => {
      if (printers.length === 0) return [];

      const requests = printers.map(async (p) => {
        try {
          const res = await apiClient.get(API_ENDPOINTS.PAPER_SIZES.LIST(p.id));
          const items = res.data?.data ?? res.data ?? [];
          return items.map((item) => ({
            ...item,
            printer_id: p.id,
            printer_name: p.name,
          }));
        } catch {
          return [];
        }
      });

      const results = await Promise.all(requests);
      return results.flat();
    },
    enabled: canReadPrinters && printers.length > 0,
  });

  // ── Query: All Paper Size Template Assignments (GET /v1/paper-sizes/{id}/templates) ──
  const {
    data: paperSizeAssignments = {},
    isLoading: assignmentsLoading,
  } = useQuery({
    queryKey: [
      "all-paper-size-assignments",
      allPaperSizes.map((p) => p.id).join(","),
    ],
    queryFn: async () => {
      if (allPaperSizes.length === 0) return {};
      const map = {};

      const requests = allPaperSizes.map(async (ps) => {
        try {
          const res = await apiClient.get(
            API_ENDPOINTS.PAPER_SIZES.TEMPLATES(ps.id)
          );
          const list = res.data?.data ?? res.data ?? [];
          map[ps.id] = Array.isArray(list) ? list : [];
        } catch {
          map[ps.id] = [];
        }
      });

      await Promise.all(requests);
      return map;
    },
    enabled: canReadPrinters && allPaperSizes.length > 0,
    staleTime: 30000,
  });

  // ── Printer Mutations ──────────────────────────────────────────────────────
  const savePrinterMutation = useMutation({
    mutationFn: async (payload) => {
      const { id, archetypeId, autoCreatePaper, ...body } = payload;

      if (id) {
        if (!canUpdatePrinters) {
          throw new Error("You do not have permission to update printers");
        }
        return apiClient.put(API_ENDPOINTS.PRINTERS.DETAILS(id), body);
      }

      if (!canCreatePrinters) {
        throw new Error("You do not have permission to create printers");
      }

      // Create printer
      const printerRes = await apiClient.post(API_ENDPOINTS.PRINTERS.LIST, body);
      const createdPrinter = printerRes.data?.data ?? printerRes.data;

      // Auto-configure initial paper size & templates if archetype selected
      if (autoCreatePaper && archetypeId && createdPrinter?.id) {
        const arch = PRINTER_ARCHETYPES.find((a) => a.id === archetypeId);
        const preset = PAPER_SIZE_PRESETS.find((p) => p.id === arch?.paperPresetId);

        if (preset) {
          try {
            const paperBody = {
              label: preset.label,
              size: preset.size,
              width: preset.width !== "" && preset.width != null ? Number(preset.width) : null,
              height: preset.height !== "" && preset.height != null ? Number(preset.height) : null,
              unit: preset.unit || "mm",
              orientation: preset.orientation || "portrait",
              is_default: true,
            };

            const paperRes = await apiClient.post(
              API_ENDPOINTS.PAPER_SIZES.LIST(createdPrinter.id),
              paperBody
            );
            const createdPaper = paperRes.data?.data ?? paperRes.data;

            // Auto-assign default templates if found
            if (createdPaper?.id && arch.defaultAssignments?.length > 0) {
              try {
                const templatesRes = await apiClient.get(API_ENDPOINTS.TEMPLATES.TEMPLATES, {
                  params: { store_id: body.store_id },
                });
                const availableTemplates = templatesRes.data?.data ?? [];

                const assignments = [];
                arch.defaultAssignments.forEach((typeVal) => {
                  const match = availableTemplates.find(
                    (t) => t.type === typeVal && t.is_active !== false
                  );
                  if (match) {
                    assignments.push({
                      template_type: typeVal,
                      template_content_id: match.id,
                      is_active: true,
                    });
                  }
                });

                if (assignments.length > 0) {
                  await apiClient.put(
                    API_ENDPOINTS.PAPER_SIZES.TEMPLATES(createdPaper.id),
                    { assignments }
                  );
                }
              } catch {
                // non-fatal
              }
            }
          } catch {
            // non-fatal paper creation
          }
        }
      }

      return printerRes;
    },
    onSuccess: (_, payload) => {
      const arch = PRINTER_ARCHETYPES.find((a) => a.id === payload.archetypeId);
      if (payload.id) {
        toast.success("Printer details updated successfully!");
      } else if (payload.autoCreatePaper && arch?.label) {
        toast.success(`Printer registered and configured with ${arch.shortLabel}!`);
      } else {
        toast.success("Printer registered successfully!");
      }

      invalidateAllPrinterQueries(queryClient);
      setPrinterModalOpen(false);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to save printer");
    },
  });

  const deletePrinterMutation = useMutation({
    mutationFn: async (id) => {
      if (!canDeletePrinters) {
        throw new Error("You do not have permission to delete printers");
      }
      return apiClient.delete(API_ENDPOINTS.PRINTERS.DETAILS(id));
    },
    onSuccess: () => {
      toast.success("Printer deleted successfully!");
      invalidateAllPrinterQueries(queryClient);
      setPrinterDeleteTarget(null);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to delete printer");
      setPrinterDeleteTarget(null);
    },
  });

  const setDefaultPrinterMutation = useMutation({
    mutationFn: async (id) => {
      if (!canUpdatePrinters) {
        throw new Error("You do not have permission to update printers");
      }
      return apiClient.post(API_ENDPOINTS.PRINTERS.SET_DEFAULT(id));
    },
    onSuccess: () => {
      toast.success("Store default printer updated!");
      invalidateAllPrinterQueries(queryClient);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to set default printer");
    },
  });

  // ── Paper Size Mutations ───────────────────────────────────────────────────
  const savePaperMutation = useMutation({
    mutationFn: async (payload) => {
      const { id, printer_id, _presetId, ...body } = payload;
      if (id && !canUpdatePrinters) {
        throw new Error("You do not have permission to update paper sizes");
      }
      if (!id && !canCreatePrinters && !canUpdatePrinters) {
        throw new Error("You do not have permission to add paper sizes");
      }

      const formattedBody = {
        label: body.label.trim(),
        size: body.size,
        width: body.width !== "" && body.width != null ? Number(body.width) : null,
        height: body.height !== "" && body.height != null ? Number(body.height) : null,
        unit: body.width !== "" && body.width != null ? (body.unit || "mm") : (body.unit || null),
        orientation: body.orientation,
        is_default: Boolean(body.is_default),
        meta: Array.isArray(body.meta) && body.meta.length > 0 ? body.meta : null,
      };

      if (id) {
        return apiClient.put(API_ENDPOINTS.PAPER_SIZES.DETAILS(printer_id, id), formattedBody);
      }
      return apiClient.post(API_ENDPOINTS.PAPER_SIZES.LIST(printer_id), formattedBody);
    },
    onSuccess: (_, payload) => {
      toast.success(`Paper size "${payload.label}" saved successfully!`);
      invalidateAllPrinterQueries(queryClient);
      setPaperModalOpen(false);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to save paper size");
    },
  });

  const deletePaperMutation = useMutation({
    mutationFn: async (target) => {
      if (!canDeletePrinters) {
        throw new Error("You do not have permission to delete paper sizes");
      }
      return apiClient.delete(API_ENDPOINTS.PAPER_SIZES.DETAILS(target.printer_id, target.id));
    },
    onSuccess: () => {
      toast.success("Paper size deleted successfully!");
      invalidateAllPrinterQueries(queryClient);
      setPaperDeleteTarget(null);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to delete paper size");
      setPaperDeleteTarget(null);
    },
  });

  const setDefaultPaperMutation = useMutation({
    mutationFn: async (ps) => {
      if (!canUpdatePrinters) {
        throw new Error("You do not have permission to update paper sizes");
      }
      return apiClient.put(API_ENDPOINTS.PAPER_SIZES.DETAILS(ps.printer_id, ps.id), {
        label: ps.label,
        size: ps.size,
        width: ps.width !== "" && ps.width != null ? Number(ps.width) : null,
        height: ps.height !== "" && ps.height != null ? Number(ps.height) : null,
        unit: ps.unit || null,
        orientation: ps.orientation || "portrait",
        is_default: true,
        meta: Array.isArray(ps.meta) ? ps.meta : null,
      });
    },
    onSuccess: (_, ps) => {
      toast.success(`Default paper size set to "${ps.label}"!`);
      invalidateAllPrinterQueries(queryClient);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Failed to set default paper size");
    },
  });

  // 1-Click Quick Add Paper Size Preset directly from empty card
  const handleQuickAddPresetPaper = async (printer, presetId, defaultDocType) => {
    if (!canCreatePrinters && !canUpdatePrinters) {
      toast.error("You do not have permission to add paper sizes");
      return;
    }
    const preset = PAPER_SIZE_PRESETS.find((p) => p.id === presetId);
    if (!preset || !printer?.id) return;

    try {
      const paperBody = {
        label: preset.label,
        size: preset.size,
        width: preset.width !== "" && preset.width != null ? Number(preset.width) : null,
        height: preset.height !== "" && preset.height != null ? Number(preset.height) : null,
        unit: preset.unit || "mm",
        orientation: preset.orientation || "portrait",
        is_default: true,
      };

      const res = await apiClient.post(
        API_ENDPOINTS.PAPER_SIZES.LIST(printer.id),
        paperBody
      );
      const newPaper = res.data?.data ?? res.data;

      // Auto-assign template if defaultDocType provided
      if (newPaper?.id && defaultDocType) {
        try {
          const templatesRes = await apiClient.get(API_ENDPOINTS.TEMPLATES.TEMPLATES, {
            params: { store_id: printer.store_id || currentStoreId },
          });
          const available = templatesRes.data?.data ?? [];
          const match = available.find(
            (t) => t.type === defaultDocType && t.is_active !== false
          );
          if (match) {
            await apiClient.put(
              API_ENDPOINTS.PAPER_SIZES.TEMPLATES(newPaper.id),
              {
                assignments: [
                  {
                    template_type: defaultDocType,
                    template_content_id: match.id,
                    is_active: true,
                  },
                ],
              }
            );
          }
        } catch {
          // ignore
        }
      }

      toast.success(`Added ${preset.label} to ${printer.name}!`);
      invalidateAllPrinterQueries(queryClient);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add preset paper size");
    }
  };

  // ── Printer Handlers ───────────────────────────────────────────────────────
  const handleAddPrinter = () => {
    if (!canCreatePrinters) {
      toast.error("You do not have permission to create printers");
      return;
    }
    setPrinterForm({
      ...BLANK_PRINTER_FORM,
      store_id: currentStore?.id
        ? { value: currentStore.id, label: currentStore.name }
        : null,
      name: "Counter Thermal Printer",
    });
    setPrinterEditMode(false);
    setPrinterModalOpen(true);
  };

  const handleEditPrinter = async (printer) => {
    if (!canUpdatePrinters) {
      toast.error("You do not have permission to edit printers");
      return;
    }
    let store_id = null;
    if (printer.store_id) {
      try {
        const res = await apiClient.get("/store", { params: { perPage: 100 } });
        const found = (res.data?.data ?? []).find((s) => s.id === printer.store_id);
        store_id = found
          ? { value: found.id, label: found.name }
          : { value: printer.store_id, label: printer.store_id };
      } catch {
        store_id = { value: printer.store_id, label: printer.store_id };
      }
    }

    setPrinterForm({
      id: printer.id,
      store_id,
      name: printer.name ?? "",
      connection_type: printer.connection_type ?? "network",
      address: printer.address ?? "",
      status: printer.status ?? "active",
      is_default: Boolean(printer.is_default),
      meta: printer.meta ?? [],
      archetypeId: "custom",
      autoCreatePaper: false,
    });
    setPrinterEditMode(true);
    setPrinterModalOpen(true);
  };

  const handleSubmitPrinter = () => {
    if (printerEditMode && !canUpdatePrinters) {
      toast.error("You do not have permission to update printers");
      return;
    }
    if (!printerEditMode && !canCreatePrinters) {
      toast.error("You do not have permission to create printers");
      return;
    }
    if (!printerForm.store_id?.value) {
      toast.error("Store is required");
      return;
    }
    if (!printerForm.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!printerForm.connection_type) {
      toast.error("Connection type is required");
      return;
    }

    const body = {
      id: printerForm.id,
      store_id: printerForm.store_id.value,
      name: printerForm.name,
      connection_type: printerForm.connection_type,
      address: printerForm.address || null,
      status: printerForm.status,
      is_default: printerForm.is_default,
      meta: printerForm.meta?.length ? printerForm.meta : null,
      archetypeId: printerForm.archetypeId,
      autoCreatePaper: printerForm.autoCreatePaper,
    };

    savePrinterMutation.mutate(body);
  };

  // ── Paper Size Handlers ────────────────────────────────────────────────────
  const handleAddPaperSize = (targetPrinter) => {
    if (!canCreatePrinters && !canUpdatePrinters) {
      toast.error("You do not have permission to add paper sizes");
      return;
    }
    const defaultPrinterId = targetPrinter?.id || printers[0]?.id || "";

    setPaperForm({
      ...BLANK_PAPER_FORM,
      printer_id: defaultPrinterId,
      label: "80mm Thermal Receipt",
      size: "custom",
      width: 3.15,
      height: 0,
      unit: "inch",
      orientation: "portrait",
    });
    setPaperEditMode(false);
    setPaperModalOpen(true);
  };

  const handleEditPaperSize = (ps) => {
    if (!canUpdatePrinters) {
      toast.error("You do not have permission to edit paper sizes");
      return;
    }
    setPaperForm({
      id: ps.id,
      printer_id: ps.printer_id,
      label: ps.label ?? "",
      size: ps.size ?? "a4",
      width: ps.width ?? "",
      height: ps.height ?? "",
      unit: ps.unit ?? "mm",
      orientation: ps.orientation ?? "portrait",
      is_default: Boolean(ps.is_default),
      meta: Array.isArray(ps.meta) ? ps.meta : [],
    });
    setPaperEditMode(true);
    setPaperModalOpen(true);
  };

  const handleSubmitPaper = () => {
    if (paperEditMode && !canUpdatePrinters) {
      toast.error("You do not have permission to update paper sizes");
      return;
    }
    if (!paperEditMode && !canCreatePrinters && !canUpdatePrinters) {
      toast.error("You do not have permission to add paper sizes");
      return;
    }
    if (!paperForm.printer_id) {
      toast.error("Please select a printer");
      return;
    }
    if (!paperForm.label.trim()) {
      toast.error("Label is required");
      return;
    }
    if (!paperForm.size) {
      toast.error("Size is required");
      return;
    }
    if (paperForm.size === "custom" && (paperForm.width === "" || paperForm.height === "")) {
      toast.error("Width and height are required for custom paper size");
      return;
    }

    savePaperMutation.mutate({ ...paperForm });
  };

  if (!canReadPrinters) {
    return <RecordNotFound />;
  }

  return (
    <PageContainer>
      {/* Page Header */}
      <PageHeader>
        <PageHeaderHeading
          title="Printers & Document Routing"
          subtitle="Configure hardware printers, paper trays, and specify which documents print to each device"
        />
        <PageHeaderActions>
          <div className="flex items-center gap-2 mr-1">
            {pageAgentLoading ? (
              <Badge variant="outline" className="text-xs py-1 px-2.5 text-muted-foreground gap-1.5 font-normal">
                <Spinner className="h-3 w-3" /> Checking Agent…
              </Badge>
            ) : pageAgentStatus?.online ? (
              <Badge
                variant="outline"
                className="text-xs py-1 px-2.5 text-emerald-600 border-emerald-500/40 bg-emerald-50/50 dark:bg-emerald-950/30 gap-1.5 font-medium cursor-pointer hover:bg-emerald-100/50 transition-colors"
                onClick={() => refetchPageAgent()}
                title="ERF Print Agent is online. Click to re-check."
              >
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                Print Agent: Online ({pageAgentStatus.count} {pageAgentStatus.count === 1 ? "device" : "devices"})
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="text-xs py-1 px-2.5 text-muted-foreground border-border bg-muted/30 gap-1.5 font-normal cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => refetchPageAgent()}
                title="ERF Print Agent not detected on localhost:9190. Click to retry."
              >
                <span className="h-2 w-2 rounded-full bg-muted-foreground/50" />
                Print Agent: Offline
              </Badge>
            )}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              await invalidateAllPrinterQueries(queryClient);
              refetchPageAgent();
              toast.success("Printers & routing data refreshed");
            }}
            className="gap-1.5 h-9 text-xs"
            title="Fetch fresh data from backend"
          >
            <RotateCw className="h-3.5 w-3.5" />
            Refresh
          </Button>

          {canCreatePrinters && (
            <Button onClick={handleAddPrinter} className="gap-1.5">
              <PlusCircle className="h-4 w-4" />
              Register Printer
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="printers" className="gap-2">
            <Printer className="h-4 w-4" />
            Printers & Hardware
          </TabsTrigger>
          <TabsTrigger value="routing" className="gap-2">
            <FileText className="h-4 w-4" />
            Document Routing Map
          </TabsTrigger>
        </TabsList>

        {/* ── Tab 1: Printers & Hardware ─────────────────────────────────── */}
        <TabsContent value="printers" className="space-y-4">
          <PrintersList
            printers={printers}
            allPaperSizes={allPaperSizes}
            paperSizeAssignments={paperSizeAssignments}
            isLoading={printersLoading || sizesLoading || assignmentsLoading}
            onAddPrinter={handleAddPrinter}
            onEditPrinter={handleEditPrinter}
            onDeletePrinter={(p) => setPrinterDeleteTarget(p)}
            onSetDefaultPrinter={(id) => setDefaultPrinterMutation.mutate(id)}
            onAddPaperSize={handleAddPaperSize}
            onEditPaperSize={handleEditPaperSize}
            onDeletePaperSize={(ps) => setPaperDeleteTarget(ps)}
            onSetDefaultPaperSize={(ps) => setDefaultPaperMutation.mutate(ps)}
            onOpenAssignments={(ps, p) => {
              setAssignmentsTargetPs(ps);
              setAssignmentsTargetPrinter(p);
            }}
            onQuickAddPresetPaper={handleQuickAddPresetPaper}
            onPreviewTemplate={setPreviewTemplate}
          />
        </TabsContent>

        {/* ── Tab 2: Document Routing Map ────────────────────────────────── */}
        <TabsContent value="routing" className="space-y-4">
          <DocumentRoutingMap
            printers={printers}
            allPaperSizes={allPaperSizes}
            paperSizeAssignments={paperSizeAssignments}
            printersLoading={printersLoading || sizesLoading}
            onOpenAssignmentsModal={(ps, p) => {
              setAssignmentsTargetPs(ps);
              setAssignmentsTargetPrinter(p);
            }}
          />
        </TabsContent>
      </Tabs>

      {/* ── Dialog: Create / Edit Printer ─────────────────────────────────── */}
      <PrinterFormDialog
        open={printerModalOpen}
        onClose={() => setPrinterModalOpen(false)}
        onSubmit={handleSubmitPrinter}
        editMode={printerEditMode}
        form={printerForm}
        setForm={setPrinterForm}
        isPending={savePrinterMutation.isPending}
      />

      {/* ── Dialog: Add / Edit Paper Size ─────────────────────────────────── */}
      <PaperSizeFormDialog
        open={paperModalOpen}
        onClose={() => setPaperModalOpen(false)}
        onSubmit={handleSubmitPaper}
        editMode={paperEditMode}
        form={paperForm}
        setForm={setPaperForm}
        printers={printers}
        hidePrinterSelect={false}
        isPending={savePaperMutation.isPending}
      />

      {/* ── Unified Template Assignments Modal ────────────────────────────── */}
      {Boolean(assignmentsTargetPs) && (
        <PaperSizeTemplateAssignmentsModal
          paperSize={assignmentsTargetPs}
          printer={assignmentsTargetPrinter}
          open={Boolean(assignmentsTargetPs)}
          onClose={() => {
            setAssignmentsTargetPs(null);
            setAssignmentsTargetPrinter(null);
          }}
        />
      )}

      {/* ── Delete Confirmation: Printer ──────────────────────────────────── */}
      <AlertDialog
        open={Boolean(printerDeleteTarget)}
        onOpenChange={(o) => !o && setPrinterDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Printer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <strong>{printerDeleteTarget?.name}</strong>? All paper size configurations and
              document assignments for this printer will also be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePrinterMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deletePrinterMutation.mutate(printerDeleteTarget.id)}
              disabled={deletePrinterMutation.isPending}
            >
              {deletePrinterMutation.isPending ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  Deleting…
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Delete Confirmation: Paper Size ───────────────────────────────── */}
      <AlertDialog
        open={Boolean(paperDeleteTarget)}
        onOpenChange={(o) => !o && setPaperDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Paper Size</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{" "}
              <strong>{paperDeleteTarget?.label}</strong>? Any documents assigned to this paper tray
              will automatically revert to your store's fallback template and default printer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePaperMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deletePaperMutation.mutate(paperDeleteTarget)}
              disabled={deletePaperMutation.isPending}
            >
              {deletePaperMutation.isPending ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  Deleting…
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Template Preview Modal ────────────────────────────────────────── */}
      {previewTemplate && (
        <TemplatePreviewModal
          template={previewTemplate}
          open={Boolean(previewTemplate)}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
    </PageContainer>
  );
};

export default Printers;
